import { Component, Inject, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable } from 'rxjs';

import { ViewsService } from '../../service/views.service';
import { UsersService } from '../../service/entity-services/users.service';
import { AuthService } from '../../service/auth.service';
import { environment } from '../../../environments/environment';
import { User } from '../../objects/interface-library';

export interface ShareViewDialogData {
  savedViews: any[];
  entityType: string;
  currentUserEmail: string;
}

@Component({
  selector: 'app-share-view',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatListModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule
  ],
  templateUrl: './share-view.component.html',
  styleUrls: ['./share-view.component.css']
})
export class ShareViewComponent implements OnInit {
  private viewsService = inject(ViewsService);
  private usersService = inject(UsersService);
  private authService = inject(AuthService);
  private snackBar = inject(MatSnackBar);

  // User selection
  allUsers: User[] = [];
  filteredUsers: User[] = [];
  selectedUser: User | null = null;
  userSearchText: string = '';

  // View selection
  selectedView: any = null;

  // UI state
  isSharing: boolean = false;
  showEmptyMessage: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<ShareViewComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ShareViewDialogData
  ) {}

  async ngOnInit() {
    // Check if we have views to share
    this.showEmptyMessage = !this.data.savedViews || this.data.savedViews.length === 0;

    // Load all users
    this.usersService.refreshAllUsersRecords();
    this.usersService.allUsers$.subscribe({
      next: (users) => {
        // Filter out the current user from the list
        this.allUsers = users.filter(user => user.email !== this.data.currentUserEmail);
        this.filteredUsers = this.allUsers;

        // If no users available, show empty message
        if (this.allUsers.length === 0) {
          this.showEmptyMessage = true;
        }
      },
      error: (err) => {
        console.error('Failed to get users:', err);
        this.allUsers = [];
        this.filteredUsers = [];
        this.showEmptyMessage = true;
      }
    });
  }

  /**
   * Filter users based on search text
   */
  filterUsers(): void {
    if (!this.userSearchText || this.userSearchText.trim() === '') {
      this.filteredUsers = this.allUsers;
      return;
    }

    const searchValue = this.userSearchText.toLowerCase().trim();
    this.filteredUsers = this.allUsers.filter(user => {
      const lastName = user.lastName?.toLowerCase() || '';
      const firstName = user.firstName?.toLowerCase() || '';
      const email = user.email?.toLowerCase() || '';
      const friendlyName = user.friendlyName?.toLowerCase() || '';

      return lastName.includes(searchValue) ||
             firstName.includes(searchValue) ||
             email.includes(searchValue) ||
             friendlyName.includes(searchValue);
    });
  }

  /**
   * Select a user from the autocomplete
   */
  onUserSelected(user: User): void {
    this.selectedUser = user;
  }

  /**
   * Select a view to share
   */
  selectView(view: any): void {
    this.selectedView = view;
  }

  /**
   * Display function for user autocomplete
   */
  displayUserFn(user: User | null): string {
    return user ? (user.friendlyName || user.email || '') : '';
  }

  /**
   * Check if the share button should be enabled
   */
  canShare(): boolean {
    return !this.isSharing &&
           this.selectedUser !== null &&
           this.selectedView !== null &&
           !this.showEmptyMessage;
  }

  /**
   * Share the selected view with the selected user
   */
  async shareViewNow() {
    if (!this.selectedUser || !this.selectedView) {
      this.snackBar.open('Please select both a user and a view', 'Close', { duration: 3000 });
      return;
    }

    this.isSharing = true;

    try {
      // The viewId format should be: "entityType.viewName"
      // For example: "contracts.My Custom View"
      const viewId = `${this.data.entityType}.${this.selectedView.name}`;

      // Use the target user's email instead of current user's email
      const targetUserEmail = this.selectedUser.email;

      console.log('Sharing view:', {
        targetUser: targetUserEmail,
        viewId: viewId,
        viewData: this.selectedView.data
      });

      // Save the view to the target user's account
      await this.viewsService.saveListView(
        environment.org,
        targetUserEmail,
        viewId,
        this.selectedView.data
      ).toPromise();

      this.snackBar.open(
        `Successfully shared "${this.selectedView.name}" with ${this.selectedUser.friendlyName || this.selectedUser.email}`,
        'Close',
        {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top'
        }
      );

      // Close the dialog
      this.dialogRef.close({ success: true });

    } catch (error) {
      console.error('Error sharing view:', error);
      this.snackBar.open(
        'Failed to share view. Please try again.',
        'Close',
        { duration: 5000 }
      );
      this.isSharing = false;
    }
  }

  /**
   * Cancel and close the dialog
   */
  cancel(): void {
    this.dialogRef.close();
  }
}
